import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Chip,
  Button,
  Card,
  CardActions,
} from "@mui/material";
import { fetchJobs as getJobs } from "../controllers/job-controllers";
import { formatDate } from "../utils/utils";
import FilterHero from "../components/filter-hero/filter-hero";
import { Link } from "@mui/material";
import { config } from "../config";
import { Helmet } from "react-helmet";
import { formatMoney, formatTimeUnit } from "../utils/formatters";
import { NotFound } from "../components/not-found/not-found";
import { useTranslate } from "../locales/use-locales";

function updateURLParams(params) {
  const url = new URL(window.location);
  Object.entries(params).forEach(([key, value]) => {
    if (
      value &&
      key !== "page" &&
      key !== "limit" &&
      key !== "postedBy" &&
      key !== "status"
    ) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
  });
  window.history.pushState({}, "", url);
}

export default function JobList({ company }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    keyword: "",
    page: 1,
    limit: 50,
    category: "",
    postedBy: {},
    postedFrom: null,
    postedTo: null,
    status: "published",
    typeOfContract: "",
    published: "",
  });

  useEffect(() => {
    const fetchJobsData = async () => {
      try {
        setLoading(true);
        const res = await getJobs(filters);
        setJobs(res.data);
        console.log(res);
        updateURLParams(filters);
      } catch (error) {
        console.error("Failed to fetch jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchJobsData, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleSearch = (searchTerm) => {
    setLoading(true);
    setFilters((prevFilters) => ({
      ...prevFilters,
      keyword: searchTerm,
    }));
  };

  const handleRemoveFilter = (filterKey) => {
    setLoading(true);
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterKey]: "",
    }));
  };

  const handleFilterChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setLoading(true);
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  return (
    <>
      <Helmet>
        <title>Sabina AI</title>
        <meta
          name="description"
          content="Browse and apply for exciting job opportunities. Find your next career move with our comprehensive job listings."
        />
        <meta
          name="keywords"
          content="jobs, careers, employment, job listings, job search"
        />
      </Helmet>
      <FilterHero
        handleSearch={handleSearch}
        handleFilterChange={handleFilterChange}
        jobLength={jobs?.length}
      />
      <FiltersBar filters={filters} onRemoveFilter={handleRemoveFilter} />
      <Container sx={{ minHeight: "60vh", mt: 4, mb: 4 }}>
        {loading ? (
          <Stack direction="row" justifyContent="center" marginTop="150px">
            <img
              src="/assets/icons/loading.svg"
              alt="loading icon"
              className="loading-icon"
            />
          </Stack>
        ) : (
          <Stack
            direction="column"
            className="contained"
            sx={{
              gap: { xs: 3, md: 0 },
            }}
          >
            {jobs.length > 0 ? (
              jobs.map((job, index) => (
                <JobCard
                  key={job._id || index}
                  job={job}
                  isFirst={index === 0}
                />
              ))
            ) : (
              <Typography variant="h6" textAlign="center" mt={4}>
                No jobs found
              </Typography>
            )}
          </Stack>
        )}
      </Container>
    </>
  );
}

function FiltersBar({ filters, onRemoveFilter }) {
  return (
    <>
      {filters.length > 0 && (
        <Stack direction="row" spacing={1} className="filters-bar contained">
          {Object.entries(filters).map(
            ([key, value]) =>
              value &&
              key !== "page" &&
              key !== "limit" &&
              key !== "postedBy" &&
              key !== "status" && (
                <Chip
                  key={key}
                  label={`${value}`}
                  onDelete={() => onRemoveFilter(key)}
                />
              )
          )}
        </Stack>
      )}
    </>
  );
}

function JobCard({ job, isFirst }) {
  const { jobDetails, createdAt, _id } = job;
  const { t } = useTranslate();
  return (
    <>
      {/* Desktop */}
      <Stack
        direction="row"
        justifyContent="space-between"
        pb={4}
        mb={4}
        borderBottom="1px solid lightgray"
        borderTop={isFirst ? "1px solid lightgray" : "none"}
        pt={isFirst ? 4 : 0}
        sx={{
          display: { xs: "none", md: "flex" },
        }}
      >
        <Stack direction="column" gap={3}>
          {jobDetails?.title && (
            <Link
              href={`job/${_id}`}
              sx={{
                textDecoration: "none",
                color: config.colors.primary,
                width: "fit-content",
                transition: "500ms color",
                cursor: "pointer",
                "&:hover": {
                  color: config.colors.secondary,
                },
              }}
            >
              <Typography variant="h3" fontWeight={600} fontSize="24px">
                {jobDetails.title}
              </Typography>
            </Link>
          )}
          <Stack direction="row" gap={4} flexWrap="wrap" mt={1}>
            {/* Location */}
            {jobDetails?.location && (
              <JobDetail icon="location" detail={jobDetails.location} />
            )}

            {!jobDetails.hideSalary && (
              // Salary
              <JobDetail
                icon="money"
                detail={
                  jobDetails?.salaryRange
                    ? `${formatMoney(
                        jobDetails.salary.from,
                        jobDetails.currency
                      )} - ${formatMoney(
                        jobDetails.salary.to,
                        jobDetails.currency
                      )} / ${formatTimeUnit(jobDetails.timeUnit)}`
                    : `${formatMoney(
                        jobDetails.salary.static,
                        jobDetails.currency
                      )}  / ${formatTimeUnit(jobDetails.timeUnit)}`
                }
              />
            )}

            {/* Type of contract */}
            {/* {jobDetails?.typeOfContract && (
              <JobDetail icon="bag" detail={jobDetails.typeOfContract} />
            )} */}
          </Stack>
        </Stack>

        <Stack
          direction="column"
          justifyContent="space-between"
          flexShrink={0}
          gap={1}
        >
          <Typography textAlign="end">{formatDate(createdAt)}</Typography>
          <a href={`job/${_id}`} className="btn">
            {t("jobList.apply")}
          </a>
        </Stack>
      </Stack>

      {/* Mobile */}
      <Card
        sx={{
          p: 3,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)", // Lighter shadow
          borderRadius: (theme) => theme.spacing(2), // Softer, rounded corners
          padding: (theme) => theme.spacing(2),
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out", // Smooth hover effects
          "&:hover": {
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)", // Slightly increased shadow on hover
            transform: "translateY(-4px)", // Subtle lift effect
          },
          display: { xs: "flex", md: "none" },
        }}
      >
        <Stack
          sx={{
            // borderBottom: "1px solid lightgray",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack>
            {/* Date created */}
            <Typography fontSize={"12px"}>{formatDate(createdAt)}</Typography>

            {/* Title */}
            {jobDetails?.title && (
              <Link
                href={`job/${_id}`}
                sx={{
                  textDecoration: "none",
                  color: config.colors.primary,
                  width: "fit-content",
                  transition: "500ms color",
                  cursor: "pointer",
                  "&:hover": {
                    color: config.colors.secondary,
                  },
                }}
              >
                <Typography variant="h3" fontSize="24px">
                  {jobDetails.title}
                </Typography>
              </Link>
            )}
          </Stack>

          {/* Info */}
          <Stack direction="row" gap={2} flexWrap="wrap">
            {jobDetails?.location && (
              <JobDetail icon="location" detail={jobDetails.location} />
            )}
          </Stack>

          {/* Ansök */}
          <Button
            fullWidth
            href={`job/${_id}`}
            variant="contained"
            // startIcon={<img src="/assets/icons/search.svg" alt="search icon" />}
            sx={{
              mb: 2,
              borderRadius: "10px",
              padding: "12px 25px",
              fontWeight: "600",
              width: "100%",
              backgroundColor: config.colors.secondary,
              color: config.colors.white,
              transition: "background-color 500ms, color 500ms",
              "&:hover": {
                background: config.colors.secondary,
                color: config.colors.white,
              },
              ":disabled": {
                backgroundColor: "white",
                opacity: 0.7,
              },
            }} // Disable button when loading
          >
            Ansök
          </Button>
        </Stack>
      </Card>
    </>
  );
}

function JobDetail({ icon, detail }) {
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <img src={`/assets/icons/${icon}.svg`} alt={`${icon} icon`} />
      <Typography fontWeight={600}>{detail}</Typography>
    </Stack>
  );
}
